/**
 * Taxonomy Service
 *
 * Handles API calls for taxonomy version management operations.
 * Part of the GO-Lab Taxonomy Management feature.
 */

import { fetchWithAuth } from '../../../../utils/api';
import type {
  TaxonomyKeySummary,
  TaxonomyVersionSummary,
  TaxonomyVersionDetail,
  TaxonomyDiff,
  DashboardData,
  PaginatedTimeline,
  TaxonomyData,
  CreateDraftOptions,
  StaleSignalsResponse,
} from '../types/taxonomy';

const BASE_PATH = '/v1/taxonomy';

// =============================================================================
// Taxonomy Key Functions
// =============================================================================

/**
 * List all available taxonomy keys with summary info
 */
export const listTaxonomyKeys = async (): Promise<TaxonomyKeySummary[]> => {
  return fetchWithAuth(`${BASE_PATH}/keys`);
};

// =============================================================================
// Query Functions
// =============================================================================

/**
 * List taxonomy versions with optional status filter and pagination
 */
export const listVersions = async (
  taxonomyKey: string,
  params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }
): Promise<TaxonomyVersionSummary[]> => {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.append('status', params.status);
  if (params?.limit) searchParams.append('limit', String(params.limit));
  if (params?.offset) searchParams.append('offset', String(params.offset));
  const query = searchParams.toString();
  return fetchWithAuth(`${BASE_PATH}/${taxonomyKey}/versions${query ? `?${query}` : ''}`);
};

/**
 * Get the current live taxonomy version with full taxonomy data
 */
export const getLiveVersion = async (
  taxonomyKey: string
): Promise<TaxonomyVersionDetail> => {
  return fetchWithAuth(`${BASE_PATH}/${taxonomyKey}/versions/live`);
};

/**
 * Get a specific taxonomy version by ID with full taxonomy data
 */
export const getVersion = async (
  taxonomyKey: string,
  versionId: string
): Promise<TaxonomyVersionDetail> => {
  return fetchWithAuth(`${BASE_PATH}/${taxonomyKey}/versions/${versionId}`);
};

/**
 * Get structural diff between two taxonomy versions
 */
export const getDiff = async (
  taxonomyKey: string,
  versionAId: string,
  versionBId: string
): Promise<TaxonomyDiff> => {
  return fetchWithAuth(
    `${BASE_PATH}/${taxonomyKey}/versions/${versionAId}/diff/${versionBId}`
  );
};

/**
 * Get audit event timeline for taxonomy operations
 */
export const getTimeline = async (
  taxonomyKey: string,
  params?: {
    limit?: number;
    offset?: number;
  }
): Promise<PaginatedTimeline> => {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.append('limit', String(params.limit));
  if (params?.offset != null) searchParams.append('offset', String(params.offset));
  const query = searchParams.toString();
  return fetchWithAuth(`${BASE_PATH}/${taxonomyKey}/timeline${query ? `?${query}` : ''}`);
};

/**
 * Get aggregated taxonomy dashboard data
 */
export const getDashboard = async (
  taxonomyKey: string
): Promise<DashboardData> => {
  return fetchWithAuth(`${BASE_PATH}/${taxonomyKey}/dashboard`);
};

/**
 * Get paginated signal IDs classified under a non-live taxonomy version
 */
export const getStaleSignals = async (
  taxonomyKey: string,
  params?: {
    limit?: number;
    offset?: number;
  }
): Promise<StaleSignalsResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.append('limit', String(params.limit));
  if (params?.offset) searchParams.append('offset', String(params.offset));
  const query = searchParams.toString();
  return fetchWithAuth(`${BASE_PATH}/${taxonomyKey}/stale-signals${query ? `?${query}` : ''}`);
};

// =============================================================================
// Mutation Functions
// =============================================================================

/**
 * Create a new draft taxonomy version.
 * Clone mode (default): copies from current live version.
 * Bootstrap mode: createNew=true with taxonomyData provided.
 */
export const createDraft = async (
  taxonomyKey: string,
  options?: CreateDraftOptions
): Promise<TaxonomyVersionSummary> => {
  return fetchWithAuth(`${BASE_PATH}/${taxonomyKey}/versions/draft`, {
    method: 'POST',
    body: JSON.stringify(options || {}),
  });
};

/**
 * Update a draft's taxonomy data
 */
export const updateDraft = async (
  taxonomyKey: string,
  versionId: string,
  taxonomyData: TaxonomyData
): Promise<TaxonomyVersionSummary> => {
  return fetchWithAuth(`${BASE_PATH}/${taxonomyKey}/versions/${versionId}`, {
    method: 'PUT',
    body: JSON.stringify({ taxonomy_data: taxonomyData }),
  });
};

/**
 * Deploy a draft version to live
 */
export const deployVersion = async (
  taxonomyKey: string,
  versionId: string,
  deploymentNote: string
): Promise<TaxonomyVersionSummary> => {
  return fetchWithAuth(`${BASE_PATH}/${taxonomyKey}/versions/${versionId}/deploy`, {
    method: 'POST',
    body: JSON.stringify({ deployment_note: deploymentNote }),
  });
};

/**
 * Rollback to a previous taxonomy version
 */
export const rollbackToVersion = async (
  taxonomyKey: string,
  versionId: string,
  note: string
): Promise<TaxonomyVersionSummary> => {
  return fetchWithAuth(`${BASE_PATH}/${taxonomyKey}/versions/${versionId}/rollback`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
};

/**
 * Delete an undeployed draft version
 */
export const deleteDraft = async (
  taxonomyKey: string,
  versionId: string
): Promise<{ ok: boolean }> => {
  return fetchWithAuth(`${BASE_PATH}/${taxonomyKey}/versions/${versionId}`, {
    method: 'DELETE',
  });
};

// =============================================================================
// Service Object
// =============================================================================

export const taxonomyService = {
  listTaxonomyKeys,
  listVersions,
  getLiveVersion,
  getVersion,
  getDiff,
  getTimeline,
  getDashboard,
  getStaleSignals,
  createDraft,
  updateDraft,
  deployVersion,
  rollbackToVersion,
  deleteDraft,
};
